import React, { useEffect, useState } from "react";
import {
  generateDaysInMonth,
  goNext,
  goPrev,
  goToday,
  handleDayChange,
  handleMonthChange,
  handleYearChange,
  setView,
} from "./../../../utils/calendar-utils";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  GalleryVertical,
  Table,
  Tally3,
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./../../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./../../../components/ui/popover";
import { cn } from "./../../../lib/utils";
import { Input } from "./../../../components/ui/input";
import { Button } from "./../../../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "./../../../components/ui/tabs";

function CalendarNav({
  viewedDate,
  calendarRef,
  startDate,
  endDate,
  setViewedDate,
}) {
  const [currentView, setCurrentView] = useState("dayGridMonth");
  const [daySelectOpen, setDaySelectOpen] = useState(false);
  const [monthSelectOpen, setMonthSelectOpen] = useState(false);

  const selectedMonth = viewedDate.getMonth() + 1;
  const selectedDay = viewedDate.getDate();
  const selectedYear = viewedDate.getFullYear();
  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const dayOptions = generateDaysInMonth(daysInMonth);

  useEffect(() => {
    if (!calendarRef?.current) return;

    const api = calendarRef.current.getApi();
    const viewType = api.view.type;

    setCurrentView(viewType);
  }, [viewedDate]);

  const months = [
    {
      value: "1",
      label: "January",
    },
    {
      value: "2",
      label: "February",
    },
    {
      value: "3",
      label: "March",
    },
    {
      value: "4",
      label: "April",
    },
    {
      value: "5",
      label: "May",
    },
    {
      value: "6",
      label: "June",
    },
    {
      value: "7",
      label: "July",
    },
    {
      value: "8",
      label: "August",
    },
    {
      value: "9",
      label: "September",
    },
    {
      value: "10",
      label: "October",
    },
    {
      value: "11",
      label: "November",
    },
    {
      value: "12",
      label: "December",
    },
  ];

  const formatMonthYear = (date) =>
    date.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });

  const formatWeekRange = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay()); // Sunday

    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    const format = (d) =>
      d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

    return `${format(start)} – ${format(end)}`;
  };

  return (
    <>
      <div
        className="relative flex min-w-full flex-col gap-4 rounded-2xl 
    border bg-white/70 px-6 py-4 shadow-sm backdrop-blur 
    md:flex-row md:items-center md:justify-evenly"
      >
        {/* 🔥 DATE RANGE PILL */}
        <div className="flex items-center">
          <p
            className={cn(
              "mt-3 inline-flex items-center justify-center rounded-full px-4 py-1.5 text-sm font-semibold shadow-sm ring-1 transition-all duration-200",
              currentView === "dayGridMonth"
                ? "bg-orange-100 text-orange-800 ring-orange-300" // 🔥 Month active
                : "bg-orange-50 text-orange-700 ring-orange-200", // 🌤 Week active
            )}
          >
            {currentView === "timeGridWeek"
              ? formatWeekRange(viewedDate)
              : formatMonthYear(viewedDate)}
          </p>
        </div>

        {/* ⬅️ NAVIGATION */}
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            className="h-9 w-9 rounded-full transition hover:bg-orange-100 hover:text-orange-700"
            onClick={() => goPrev(calendarRef)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Month Selector */}
          <Popover open={monthSelectOpen} onOpenChange={setMonthSelectOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-9 min-w-[130px] justify-between rounded-full 
            border-muted bg-white text-xs font-semibold shadow-sm"
              >
                {months?.find((m) => m.value === String(selectedMonth))
                  ?.label || "Select month"}
                <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[220px] rounded-xl p-0 shadow-lg">
              <Command>
                <CommandInput placeholder="Search month..." />
                <CommandList>
                  <CommandEmpty>No month found.</CommandEmpty>
                  <CommandGroup>
                    {months.map((month) => (
                      <CommandItem
                        key={month.value}
                        value={month.value}
                        onSelect={(value) => {
                          handleMonthChange(calendarRef, viewedDate, value);
                          setMonthSelectOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            String(selectedMonth) === month.value
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        {month.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Year */}
          <Input
            type="number"
            className="h-9 w-[90px] rounded-full border-muted 
        bg-white text-center text-xs font-semibold shadow-sm"
            value={selectedYear}
            onChange={(e) =>
              handleYearChange(calendarRef, viewedDate, e.target.value)
            }
          />

          <Button
            variant="ghost"
            className="h-9 w-9 rounded-full transition hover:bg-orange-100 hover:text-orange-700"
            onClick={() => goNext(calendarRef)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* 🎛️ CONTROLS */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button
            className={cn(
              "h-9 rounded-full text-xs font-semibold shadow-sm transition-all",
              currentView === "dayGridMonth"
                ? "bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200"
                : "hover:bg-orange-50",
            )}
            variant="outline"
            onClick={() => goToday(calendarRef)}
          >
            {currentView === "timeGridDay"
              ? "Today"
              : currentView === "timeGridWeek"
                ? "This Week"
                : "This Month"}
          </Button>

          <Tabs defaultValue="dayGridMonth">
            <TabsList className="flex h-9 rounded-full bg-orange-50/50 p-1">
              <TabsTrigger
                value="timeGridWeek"
                onClick={() =>
                  setView(calendarRef, "timeGridWeek", setCurrentView)
                }
                className={cn(
                  "rounded-full px-4 text-xs transition-all",
                  currentView === "timeGridWeek"
                    ? "bg-orange-200 text-orange-900 shadow-sm"
                    : "hover:bg-orange-100",
                )}
              >
                <Tally3 className="mr-1 h-4 w-4" />
                Week
              </TabsTrigger>

              <TabsTrigger
                value="dayGridMonth"
                onClick={() =>
                  setView(calendarRef, "dayGridMonth", setCurrentView)
                }
                className={cn(
                  "rounded-full px-4 text-xs transition-all",
                  currentView === "dayGridMonth"
                    ? "bg-orange-300 text-orange-900 shadow-sm"
                    : "hover:bg-orange-100",
                )}
              >
                <Table className="mr-1 h-4 w-4 rotate-90" />
                Month
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
    </>
  );
}

export default CalendarNav;
